"use client";

import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
} from "react";
import Cookies from "js-cookie";
import { GetActiveHolidayNotices } from "@/app/actions/getHolidayNotices";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FaBell, FaTimes, FaArrowRight, FaArrowLeft } from "react-icons/fa";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";

// Define interface for multilingual content
interface MultilingualContent {
  en: string;
  zh: string;
  cn: string;
}

// Define interface for holiday notice
interface HolidayNotice {
  _id: string;
  title: MultilingualContent;
  content: MultilingualContent;
  isActive: boolean;
  startDate: string | Date;
  endDate: string | Date;
  cookieName: string;
  cookieExpiryDays: number;
  showOnce: boolean;
}

// Context for controlling the notice
interface HolidayNoticeContextType {
  showNotice: () => void;
  hideNotice: () => void;
}

export const HolidayNoticeContext = createContext<HolidayNoticeContextType>({
  showNotice: () => {
    console.log("Default showNotice called - not yet initialized");
  },
  hideNotice: () => {},
});

export const useHolidayNotice = () => useContext(HolidayNoticeContext);

// Custom DialogContent without the built-in close button
const CustomDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg",
        className
      )}
      {...props}>
      {children}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
CustomDialogContent.displayName = "CustomDialogContent";

// Function to determine language from path
const getLanguageFromPath = (path: string): "en" | "zh" | "cn" => {
  if (path.startsWith("/zh")) return "zh";
  if (path.startsWith("/cn")) return "cn";
  return "en"; // Default to English
};

export default function HolidayNoticeModal() {
  // State variables
  const [notices, setNotices] = useState<HolidayNotice[]>([]);
  const [noticeQueue, setNoticeQueue] = useState<HolidayNotice[]>([]);
  const [currentNotice, setCurrentNotice] = useState<HolidayNotice | null>(
    null
  );
  const [isOpen, setIsOpen] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState<"en" | "zh" | "cn">(
    "en"
  );
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [hasActiveNotices, setHasActiveNotices] = useState(false);
  const [noticeIndex, setNoticeIndex] = useState(0);
  const [allNotices, setAllNotices] = useState<HolidayNotice[]>([]);

  // Use ref to store notices for reliable access in callbacks
  const allNoticesRef = useRef<HolidayNotice[]>([]);

  // Get current path for language detection
  const pathname = usePathname();

  // Set language based on URL path
  useEffect(() => {
    const pathLang = getLanguageFromPath(pathname);
    setActiveLanguage(pathLang);
    console.log("Language set from URL path:", pathLang);
  }, [pathname]);

  // Update language when path changes
  useEffect(() => {
    const pathLang = getLanguageFromPath(pathname);
    if (activeLanguage !== pathLang) {
      setActiveLanguage(pathLang);
      console.log("Language updated due to path change:", pathLang);
    }
  }, [pathname, activeLanguage]);

  // Fetch notices on component mount
  useEffect(() => {
    const getNotices = async () => {
      try {
        const activeNotices = await GetActiveHolidayNotices();

        if (activeNotices && activeNotices.length > 0) {
          setNotices(activeNotices);
          setAllNotices(activeNotices);
          allNoticesRef.current = activeNotices; // Store in ref for reliable access
          setHasActiveNotices(true);
          console.log(
            "HolidayNoticeModal - Fetched notices:",
            activeNotices.length,
            "notices available"
          );

          // Filter notices not yet dismissed
          const filteredNotices = activeNotices.filter(
            (notice: HolidayNotice) => {
              const cookieName = notice.cookieName || "holiday-notice";
              const cookieValue = Cookies.get(cookieName);
              return !cookieValue || !notice.showOnce;
            }
          );

          setNoticeQueue(filteredNotices);

          // Show only first notice if available
          if (filteredNotices.length > 0) {
            setCurrentNotice(filteredNotices[0]);
            setNoticeIndex(0);
            setIsOpen(true);

            // Set language based on URL path when first showing notice
            const pathLang = getLanguageFromPath(pathname);
            setActiveLanguage(pathLang);
            // Only show the first one, don't auto-show the rest
          } else if (activeNotices.length > 0) {
            // Set the first notice as current even if all have been dismissed
            setCurrentNotice(activeNotices[0]);
            setNoticeIndex(0);
          }
        }
      } catch (error) {
        console.error("Error fetching notices:", error);
      }
    };

    getNotices();
  }, [pathname]);

  // Keep ref in sync with state
  useEffect(() => {
    allNoticesRef.current = allNotices;
  }, [allNotices]);

  // Handle notice dismissal
  const handleDismiss = (skipCookie = false) => {
    if (currentNotice && !skipCookie) {
      const cookieName = currentNotice.cookieName || "holiday-notice";

      if (dontShowAgain) {
        const expiryDays = currentNotice.showOnce
          ? currentNotice.cookieExpiryDays || 1
          : 1;

        Cookies.set(cookieName, "dismissed", {
          expires: expiryDays,
          sameSite: "strict",
          secure: window.location.protocol === "https:",
        });
      }

      // Remove current notice from queue
      const updatedQueue = noticeQueue.filter(
        (notice) => notice._id !== currentNotice._id
      );
      setNoticeQueue(updatedQueue);

      // Close current modal
      setIsOpen(false);
      setDontShowAgain(false);

      // Don't automatically show next notice on dismiss
      // Let user reopen from bell icon if they want to see more
    } else {
      // Just close the modal without cookie handling
      setIsOpen(false);
    }
  };

  // Handle next notice
  const handleNextNotice = () => {
    console.log("Next notice clicked", { noticeIndex, allNotices });

    if (allNotices.length > 1 && noticeIndex < allNotices.length - 1) {
      const nextIndex = noticeIndex + 1;
      setNoticeIndex(nextIndex);
      setCurrentNotice(allNotices[nextIndex]);
      setDontShowAgain(false);

      // Refresh language when navigating between notices
      const pathLang = getLanguageFromPath(pathname);
      setActiveLanguage(pathLang);
    }
  };

  // Handle previous notice
  const handlePrevNotice = () => {
    console.log("Prev notice clicked", { noticeIndex, allNotices });

    if (noticeIndex > 0) {
      const prevIndex = noticeIndex - 1;
      setNoticeIndex(prevIndex);
      setCurrentNotice(allNotices[prevIndex]);
      setDontShowAgain(false);

      // Refresh language when navigating between notices
      const pathLang = getLanguageFromPath(pathname);
      setActiveLanguage(pathLang);
    }
  };

  // Context methods
  const showNotice = () => {
    console.log("HolidayNoticeModal.showNotice called", {
      allNoticesCount: allNoticesRef.current.length,
      currentNotice: currentNotice ? currentNotice._id : null,
      isModalOpen: isOpen,
    });

    if (allNoticesRef.current.length > 0) {
      // Get notices directly from ref for reliability
      const firstNotice = allNoticesRef.current[0];
      console.log("Setting currentNotice to:", firstNotice?._id);

      // Force reopen the modal
      setIsOpen(false);
      setCurrentNotice(firstNotice);
      setNoticeIndex(0);

      // Set language based on URL path when showing notice from header
      const pathLang = getLanguageFromPath(pathname);
      setActiveLanguage(pathLang);

      // Use setTimeout to ensure state updates have propagated
      setTimeout(() => {
        console.log("Opening dialog with:", firstNotice?._id);
        setIsOpen(true);
        console.log("Modal should now be open:", true);
      }, 50);
    } else {
      console.log("No notices available to show");
    }
  };

  const hideNotice = () => {
    setIsOpen(false);
    console.log("Modal should now be closed");
  };

  // Add event listener for custom openHolidayNotice event
  useEffect(() => {
    const handleOpenEvent = () => {
      console.log("Received openHolidayNotice event");
      if (allNoticesRef.current.length > 0) {
        // Get notices directly from ref for reliability
        const firstNotice = allNoticesRef.current[0];
        console.log(
          "Event listener setting currentNotice to:",
          firstNotice?._id
        );

        // Force reopen the modal
        setIsOpen(false);
        setCurrentNotice(firstNotice);
        setNoticeIndex(0);

        // Set language based on URL path when opening from event
        const pathLang = getLanguageFromPath(pathname);
        setActiveLanguage(pathLang);

        // Use setTimeout to ensure state updates have propagated
        setTimeout(() => {
          console.log("Event listener opening dialog with:", firstNotice?._id);
          setIsOpen(true);
          console.log("Modal should now be open from event:", true);
        }, 50);
      }
    };

    document.addEventListener("openHolidayNotice", handleOpenEvent);
    console.log("Added openHolidayNotice event listener");

    return () => {
      document.removeEventListener("openHolidayNotice", handleOpenEvent);
      console.log("Removed openHolidayNotice event listener");
    };
  }, [pathname]);

  // Get language-specific texts for labels
  const getLanguageText = () => {
    const texts = {
      dontShowAgain:
        activeLanguage === "en"
          ? "Don't show again today"
          : activeLanguage === "zh"
          ? "今天不再顯示"
          : "今天不再显示",
      dismiss:
        activeLanguage === "en"
          ? "Dismiss"
          : activeLanguage === "zh"
          ? "關閉"
          : "关闭",
      prev:
        activeLanguage === "en"
          ? "Prev"
          : activeLanguage === "zh"
          ? "上一個"
          : "上一个",
      next:
        activeLanguage === "en"
          ? "Next"
          : activeLanguage === "zh"
          ? "下一個"
          : "下一个",
    };
    return texts;
  };

  return (
    <HolidayNoticeContext.Provider
      value={{
        showNotice,
        hideNotice,
      }}>
      {/* Holiday Notice Modal */}
      {currentNotice && (
        <Dialog
          key={`dialog-${activeLanguage}-${currentNotice._id}`}
          open={isOpen}
          modal={true}
          onOpenChange={(open) => {
            console.log(
              "Dialog onOpenChange:",
              open,
              "currentNotice:",
              currentNotice?._id
            );
            if (!open) {
              // Just hide the dialog without changing currentNotice
              setIsOpen(false);
              console.log("Dialog closed, keeping currentNotice for reopening");
            }
          }}>
          <CustomDialogContent
            className="sm:max-w-md bg-white dark:bg-gray-800 shadow-xl rounded-lg border-0 max-h-[90vh] flex flex-col"
            forceMount={true}>
            <DialogHeader className="border-b pb-3 relative">
              <div className="flex items-center gap-2 text-primary">
                <FaBell className="h-5 w-5" />
                <DialogTitle className="text-xl font-semibold">
                  {currentNotice.title[activeLanguage]}
                </DialogTitle>
              </div>
              <button
                onClick={() => handleDismiss(true)}
                className="absolute right-2 top-2 rounded-full p-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                aria-label="Close">
                <FaTimes className="h-4 w-4" />
              </button>
            </DialogHeader>

            <div className="py-4 overflow-y-auto flex-grow">
              <div
                className="prose dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{
                  __html: currentNotice.content[activeLanguage],
                }}
              />
            </div>

            <DialogFooter
              key={`footer-${activeLanguage}`}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-3 border-t pt-4 mt-auto">
              <div className="flex items-center space-x-2 min-w-[180px] whitespace-nowrap">
                <Checkbox
                  id="dontShowAgain"
                  checked={dontShowAgain}
                  onCheckedChange={(checked) => setDontShowAgain(!!checked)}
                />
                <label
                  htmlFor="dontShowAgain"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {(() => getLanguageText().dontShowAgain)()}
                </label>
              </div>

              <div className="flex justify-between w-full items-center">
                {/* Notice navigation indicators */}
                <div className="flex items-center gap-2">
                  {allNotices.length > 1 && (
                    <div className="flex items-center gap-2">
                      {noticeIndex > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handlePrevNotice}
                          className="h-8 px-2 gap-0.5">
                          <FaArrowLeft className="h-3 w-3" />
                          <span className="hidden sm:inline ml-1 whitespace-nowrap">
                            {(() => getLanguageText().prev)()}
                          </span>
                        </Button>
                      )}

                      <span className="text-sm text-muted-foreground px-2">
                        {noticeIndex + 1}/{allNotices.length}
                      </span>

                      {noticeIndex < allNotices.length - 1 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleNextNotice}
                          className="h-8 px-2 gap-0.5">
                          <span className="hidden sm:inline mr-1 whitespace-nowrap">
                            {(() => getLanguageText().next)()}
                          </span>
                          <FaArrowRight className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Dismiss button */}
                <Button
                  onClick={() => handleDismiss(false)}
                  className="bg-primary hover:bg-primary/90">
                  {(() => getLanguageText().dismiss)()}
                </Button>
              </div>
            </DialogFooter>
          </CustomDialogContent>
        </Dialog>
      )}
    </HolidayNoticeContext.Provider>
  );
}
